import React, { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { u2000LogicalUnitPathPrefix, u2000QuickSearchSkipDefaultFilterTransition } from "../../tools/U2000QuickSearchUtils";

export type BreadcrumbItem = { label: string; href?: string };

export interface QuickSearchData {
  fieldName: string;
  condition: string;
  value: string;
  isFromQuickSearch?: boolean;
  targetLogicalUnit?: string; // Identifiant de l'unité logique cible (ex: "/U2101", "/U2103")
}

export interface U2000LogicalUnitState {
  logicalUnitTitle: string | null;
  logicalUnitBreadcrumb: BreadcrumbItem[];
  quickSearchData: QuickSearchData | null;
  /** true tant qu’on est sur l’UL cible d’un Quick Search (filtre sauvegardé ignoré). */
  quickSearchSkipDefaultFilter: boolean;
}

export interface U2000LogicalUnitContextType extends U2000LogicalUnitState {
  setLogicalUnitTitle: (title: string | null) => void;
  setLogicalUnitBreadcrumb: (crumbs: BreadcrumbItem[]) => void;
  setQuickSearchData: (data: QuickSearchData | null) => void;
  getQuickSearchData: () => QuickSearchData | null;
}

const defaultValue: U2000LogicalUnitContextType = {
  logicalUnitTitle: null,
  logicalUnitBreadcrumb: [],
  quickSearchData: null,
  quickSearchSkipDefaultFilter: false,
  setLogicalUnitTitle: () => {},
  setLogicalUnitBreadcrumb: () => {},
  setQuickSearchData: () => {},
  getQuickSearchData: () => null,
};

const U2000LogicalUnitContext = createContext<U2000LogicalUnitContextType | undefined>(undefined);

export const U2000LogicalUnitProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [logicalUnitTitle, setLogicalUnitTitle] = useState<string | null>(null);
  const [logicalUnitBreadcrumb, setLogicalUnitBreadcrumb] = useState<BreadcrumbItem[]>([]);
  const [quickSearchData, setQuickSearchDataState] = useState<QuickSearchData | null>(null);
  const [quickSearchSkipDefaultFilter, setQuickSearchSkipDefaultFilter] = useState(false);
  const skipTargetPrefixRef = useRef<string | null>(null);
  const hasLandedOnQsTargetRef = useRef(false);

  const setQuickSearchData = (data: QuickSearchData | null) => {
    setQuickSearchDataState(data);
    if (data) {
      setQuickSearchSkipDefaultFilter(true);
      skipTargetPrefixRef.current = u2000LogicalUnitPathPrefix(data.targetLogicalUnit ?? '');
      hasLandedOnQsTargetRef.current = false;
    }
  };

  useEffect(() => {
    const next = u2000QuickSearchSkipDefaultFilterTransition({
      targetPrefix: skipTargetPrefixRef.current,
      currentPrefix: u2000LogicalUnitPathPrefix(location.pathname),
      hasLandedOnTarget: hasLandedOnQsTargetRef.current,
    });
    hasLandedOnQsTargetRef.current = next.hasLandedOnTarget;
    if (next.releaseSkip) {
      skipTargetPrefixRef.current = null;
      setQuickSearchSkipDefaultFilter(false);
    }
  }, [location.pathname]);

  const getQuickSearchData = () => quickSearchData;

  const value = useMemo(
    () => ({
      logicalUnitTitle,
      logicalUnitBreadcrumb,
      quickSearchData,
      quickSearchSkipDefaultFilter,
      setLogicalUnitTitle,
      setLogicalUnitBreadcrumb,
      setQuickSearchData,
      getQuickSearchData,
    }),
    [logicalUnitTitle, logicalUnitBreadcrumb, quickSearchData, quickSearchSkipDefaultFilter]
  );

  return <U2000LogicalUnitContext.Provider value={value ?? defaultValue}>{children}</U2000LogicalUnitContext.Provider>;
};

export const useU2000LogicalUnitContext = (): U2000LogicalUnitContextType => {
  const ctx = useContext(U2000LogicalUnitContext);
  if (!ctx) throw new Error("useU2000LogicalUnitContext must be used within U2000LogicalUnitProvider");
  return ctx;
};

export default U2000LogicalUnitContext;
